import React, { useState, useEffect } from 'react';
import { FaRedo, FaTimes, FaCheck, FaExclamationTriangle, FaCalendarAlt, FaPaperclip, FaInfoCircle, FaCheckSquare, FaSquare } from 'react-icons/fa';
import { useData } from '../contexts/DataContext';
import '../styles/RedoCaseModal.css';

const REDO_REASONS = [
    "Ajustage / Bascule / Problème d'insertion",
    "Teinte / Esthétique non conforme",
    "Occlusion / Dimension verticale trop haute",
    "Empreinte ou modèle imprécis",
    "Fracture / Fêlure / Ébréchure",
    "Modification de préparation par le praticien",
    "Point de contact trop lâche / serré",
    "Autre motif"
];

function RedoCaseModal({ isOpen, onClose, order, onSuccess }) {
    const { addOrder, updateOrder, products } = useData();

    const [items, setItems] = useState([]);
    const [selectedReason, setSelectedReason] = useState(REDO_REASONS[0]);
    const [customReason, setCustomReason] = useState('');
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [newDeliveryDate, setNewDeliveryDate] = useState('');
    const [keepFiche, setKeepFiche] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Normalize and prepare order items when modal opens
    useEffect(() => {
        if (!isOpen || !order) return;

        const normalized = [];

        if (order.teethConfig) {
            if (Array.isArray(order.teethConfig)) {
                order.teethConfig.forEach((line, idx) => {
                    const prod = products.find(p => p.id === line.productId);
                    const teethArr = Array.isArray(line.teeth) ? line.teeth : (line.teeth ? [line.teeth] : []);
                    const teethLabel = teethArr.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ') || '-';
                    normalized.push({
                        key: line.id || `line-${idx}`,
                        teeth: teethArr,
                        teethLabel: teethLabel,
                        productId: line.productId,
                        productName: prod ? prod.name : (line.name || 'Prestation'),
                        category: prod ? prod.category : 'Prothèse',
                        details: line.details || {},
                        originalNotes: line.notes || '',
                        correctionNotes: '',
                        selected: true // Selected by default
                    });
                });
            } else if (typeof order.teethConfig === 'object') {
                Object.entries(order.teethConfig).forEach(([toothId, conf], idx) => {
                    const prod = products.find(p => p.id === conf.productId);
                    normalized.push({
                        key: `obj-${toothId}-${idx}`,
                        teeth: [toothId],
                        teethLabel: toothId === 'HAUT' ? 'Haut' : toothId === 'BAS' ? 'Bas' : toothId,
                        productId: conf.productId,
                        productName: prod ? prod.name : 'Prestation',
                        category: prod ? prod.category : 'Prothèse',
                        details: conf.details || {},
                        originalNotes: conf.notes || '',
                        correctionNotes: '',
                        selected: true
                    });
                });
            }
        }

        setItems(normalized);
        setSelectedReason(REDO_REASONS[0]);
        setCustomReason('');
        setAdditionalNotes('');
        setNewDeliveryDate('');
        setKeepFiche(Boolean(order.ficheUrl || (order.fiches && order.fiches.length > 0)));
        setErrorMsg('');
    }, [isOpen, order, products]);

    if (!isOpen || !order) return null;

    const selectedCount = items.filter(it => it.selected).length;
    const allSelected = items.length > 0 && selectedCount === items.length;

    const toggleSelectAll = () => {
        const nextState = !allSelected;
        setItems(prev => prev.map(it => ({ ...it, selected: nextState })));
    };

    const toggleItem = (key) => {
        setItems(prev => prev.map(it => it.key === key ? { ...it, selected: !it.selected } : it));
    };

    const updateItemCorrectionNotes = (key, text) => {
        setItems(prev => prev.map(it => it.key === key ? { ...it, correctionNotes: text } : it));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        const selectedItems = items.filter(it => it.selected);
        if (selectedItems.length === 0) {
            setErrorMsg('Veuillez sélectionner au moins un élément à refaire.');
            return;
        }

        const finalReason = selectedReason === 'Autre motif' 
            ? (customReason.trim() || 'Non spécifié')
            : selectedReason;

        setIsSubmitting(true);

        try {
            // Build the teethConfig for the redo case:
            // Crucial: quantity is explicitly 0 to enforce 0,00 € billing under warranty!
            const newTeethConfig = selectedItems.map((item, idx) => {
                const noteParts = [];
                if (item.correctionNotes.trim()) {
                    noteParts.push(`[REFAIT] ${item.correctionNotes.trim()}`);
                }
                if (item.originalNotes.trim()) {
                    noteParts.push(`(Origine: ${item.originalNotes.trim()})`);
                }

                return {
                    id: `line-${Date.now()}-${idx}`,
                    productId: item.productId,
                    teeth: item.teeth,
                    name: item.productName,
                    details: { ...item.details },
                    notes: noteParts.join(' • '),
                    quantity: 0, // 0 € guarantee
                    isRedo: true
                };
            });

            const originalCaseNum = order.caseNumber || (typeof order.id === 'string' ? order.id.substring(0, 8).toUpperCase() : 'ENO-INCONNU');
            
            // Build global notes for the new redo case
            const noteSegments = [
                `🔄 [REDO du cas ${originalCaseNum}]`,
                `Motif : ${finalReason}`
            ];
            if (additionalNotes.trim()) {
                noteSegments.push(`Remarques : ${additionalNotes.trim()}`);
            }

            const redoOrderPayload = {
                clientId: order.clientId || null,
                practitionerName: order.practitionerName || '',
                patientName: order.patientName || '',
                patientReference: order.patientReference || '',
                gamme: order.gamme || 'classique',
                appointmentDate: newDeliveryDate || null,
                status: 'Reçu',
                teethConfig: newTeethConfig,
                notes: noteSegments.join('\n'),
                ficheUrl: keepFiche ? (order.ficheUrl || null) : null,
                fiches: keepFiche ? (order.fiches || null) : null,
                priority: 'normal'
            };

            const createdOrder = await addOrder(redoOrderPayload);

            // Update original order's notes to keep trace of the redo
            try {
                const createdCaseNum = createdOrder?.caseNumber || 'Nouveau cas';
                const originalExistingNotes = order.notes || '';
                const redoTrace = `\n[🔄 Refait créé : ${createdCaseNum} le ${new Date().toLocaleDateString('fr-FR')}]`;
                await updateOrder(order.id, {
                    notes: (originalExistingNotes + redoTrace).trim()
                });
            } catch (traceErr) {
                console.warn('Could not update original case trace:', traceErr);
            }

            if (onSuccess) {
                onSuccess(createdOrder);
            }
            onClose();
        } catch (err) {
            console.error('Erreur lors de la création du Redo:', err);
            setErrorMsg("Une erreur est survenue lors de la création de la fiche Redo. Veuillez réessayer.");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="redo-modal-backdrop" onClick={onClose}>
            <div className="redo-modal-card animate__animated animate__zoomIn" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="redo-modal-header">
                    <div className="redo-modal-title-area">
                        <div className="redo-modal-icon-badge">
                            <FaRedo className="redo-spin-icon" />
                        </div>
                        <div>
                            <h3 className="redo-modal-title">Créer un travail à refaire (Redo)</h3>
                            <p className="redo-modal-subtitle">
                                Cas d'origine : <strong className="text-dark font-monospace">{order.caseNumber || order.id}</strong> • Patient : <strong>{order.patientName}</strong> • Dr. {order.practitionerName}
                            </p>
                        </div>
                    </div>
                    <button type="button" className="btn-redo-close" onClick={onClose} title="Fermer">
                        <FaTimes />
                    </button>
                </div>

                {/* Info banner */}
                <div className="redo-alert-banner">
                    <FaInfoCircle className="flex-shrink-0" size={18} />
                    <div>
                        <strong>Nouvelle fiche sous garantie :</strong> Un nouveau numéro de cas sera généré. La quantité des éléments validés sera fixée à <strong>0 (0,00 €)</strong> pour ne pas facturer le praticien.
                    </div>
                </div>

                {errorMsg && (
                    <div className="alert alert-danger mx-4 my-2 d-flex align-items-center gap-2">
                        <FaExclamationTriangle />
                        <span>{errorMsg}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="redo-modal-body">
                    {/* Step 1: Validation des éléments */}
                    <div className="redo-section">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="redo-section-title">
                                1. Valider les éléments / prestations à refaire
                            </label>
                            {items.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-decoration-none p-0 fw-semibold"
                                    onClick={toggleSelectAll}
                                >
                                    {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                </button>
                            )}
                        </div>

                        {items.length === 0 ? (
                            <div className="p-3 text-center text-muted border rounded bg-light">
                                Aucun élément technique n'a été détecté dans le cas d'origine.
                            </div>
                        ) : (
                            <div className="redo-items-list">
                                {items.map(item => (
                                    <div
                                        key={item.key}
                                        className={`redo-item-card ${item.selected ? 'selected' : 'unselected'}`}
                                        onClick={() => toggleItem(item.key)}
                                    >
                                        <div className="redo-item-header">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="redo-checkbox">
                                                    {item.selected ? (
                                                        <FaCheckSquare className="text-primary" size={20} />
                                                    ) : (
                                                        <FaSquare className="text-muted" size={20} />
                                                    )}
                                                </div>
                                                <div>
                                                    <span className="badge bg-primary text-white me-2 px-2.5 py-1.5 fw-bold">
                                                        Dent {item.teethLabel}
                                                    </span>
                                                    <strong className="text-dark">{item.productName}</strong>
                                                    <span className="text-muted small ms-2">({item.category})</span>
                                                </div>
                                            </div>
                                            <span className={`badge ${item.selected ? 'bg-warning text-dark border border-warning' : 'bg-light text-muted'}`}>
                                                {item.selected ? '🔄 Qté: 0 (0,00 €)' : 'Exclu'}
                                            </span>
                                        </div>

                                        <div className="redo-item-details">
                                            {item.details?.teinte && (
                                                <span className="badge bg-light text-dark border me-2">
                                                    Teinte: {item.details.teinte}
                                                </span>
                                            )}
                                            {item.details?.materiau && (
                                                <span className="badge bg-light text-dark border me-2">
                                                    Matériau: {item.details.materiau}
                                                </span>
                                            )}
                                            {item.originalNotes && (
                                                <span className="text-muted small fst-italic">
                                                    Note origine : {item.originalNotes}
                                                </span>
                                            )}
                                        </div>

                                        {item.selected && (
                                            <div className="redo-item-note-input" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Instruction / correction pour cette dent (ex: Teinte A1 au lieu de A2, rehausser 0.5mm...)"
                                                    value={item.correctionNotes}
                                                    onChange={(e) => updateItemCorrectionNotes(item.key, e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-2 px-1 text-muted small">
                            <span>Éléments sélectionnés : <strong>{selectedCount}</strong> sur <strong>{items.length}</strong></span>
                            <span className="text-success fw-bold">Tarif total : 0,00 € (Sous garantie)</span>
                        </div>
                    </div>

                    {/* Step 2: Motif du refait */}
                    <div className="redo-section">
                        <label className="redo-section-title">
                            2. Motif & Raison du travail à refaire
                        </label>
                        <div className="row g-2 mb-2">
                            <div className="col-md-6">
                                <select
                                    className="form-select"
                                    value={selectedReason}
                                    onChange={(e) => setSelectedReason(e.target.value)}
                                >
                                    {REDO_REASONS.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>
                            {selectedReason === 'Autre motif' && (
                                <div className="col-md-6">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Préciser le motif..."
                                        value={customReason}
                                        onChange={(e) => setCustomReason(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>

                        <textarea
                            className="form-control mt-2"
                            rows="2"
                            placeholder="Instructions générales ou remarques pour l'atelier / technicien..."
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                        />
                    </div>

                    {/* Step 3: Date & Options */}
                    <div className="redo-section">
                        <label className="redo-section-title">
                            3. Nouvelle date de livraison & Options
                        </label>
                        <div className="row g-3 align-items-center">
                            <div className="col-sm-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><FaCalendarAlt className="text-primary" /></span>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={newDeliveryDate}
                                        onChange={(e) => setNewDeliveryDate(e.target.value)}
                                        title="Nouvelle date de livraison souhaitée"
                                    />
                                </div>
                                <small className="text-muted d-block mt-1">Nouvelle date d'essayage / livraison souhaitée</small>
                            </div>
                            {(order.ficheUrl || (order.fiches && order.fiches.length > 0)) && (
                                <div className="col-sm-6">
                                    <div className="form-check pt-1">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="keepFicheCheck"
                                            checked={keepFiche}
                                            onChange={(e) => setKeepFiche(e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor="keepFicheCheck">
                                            <FaPaperclip className="me-1 text-muted" /> Transférer les fiches/documents d'origine {order.fiches?.length > 1 ? `(${order.fiches.length})` : ''}
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="redo-modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-3 py-2 fw-semibold"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="btn btn-warning px-4 py-2 fw-bold text-dark d-flex align-items-center gap-2 shadow-sm"
                            disabled={isSubmitting || selectedCount === 0}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    <span>Création du Redo...</span>
                                </>
                            ) : (
                                <>
                                    <FaRedo />
                                    <span>Valider et créer le Redo (0,00 €)</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default RedoCaseModal;
